import { RefObject } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Search, Filter, X } from 'lucide-react';
import SearchableSelect from '../../components/ui/SearchableSelect';
import {
  FilterSection,
  FilterGrid,
  FilterField,
  filterControlClass,
} from '../../components/filters/FilterSection';
import { useMenuSettings } from '../../contexts/MenuSettingsContext';
import { APPROVAL_GATE_STATUS_OPTIONS } from './constants';
import type { DeliveryMetricMode, OTIFGradeThreshold } from '../../lib/api';
import { showsOtif } from '../../components/dashboard/deliveryMetricMode';
import { useFilterVisibility } from '../../hooks/useFilterVisibility';

/**
 * Placeholder shown in place of a picker whose option list came back empty.
 * Matches `filterControlClass` geometry so an unavailable field keeps the same
 * footprint as a live one and the grid rows stay level.
 */
const EMPTY_OPTION_CLASS =
  'w-full px-3 py-2.5 border border-border rounded-md bg-muted/30 text-muted-foreground text-sm italic cursor-not-allowed';

/** CR-3953: Filter options for the supplier list */
export interface SupplierFilters {
  site: string;
  status: string;
  supplier_code: string;
  commodity_group: string;
  commodity_type: string;
  strategic: string;
  risk: string;
  questionnaire: string;
  otif_band: string; // Trailing-12-month OTIF band: a grade id from the OTIF Grade Thresholds
  approval_gate_status: string; // CR-5556: Approval Gate Status filter
  // Deactivated suppliers are hidden by default — they are usually retired
  // duplicates. But an inactive supplier is invisible everywhere while still
  // owning live POs, and its code is dropped from the ERP feed filter, so
  // there has to be a way to see one in order to diagnose or reverse it.
  include_inactive: string; // '' = active only, 'yes' = include inactive
}

export const EMPTY_FILTERS: SupplierFilters = {
  site: '',
  status: '',
  supplier_code: '',
  commodity_group: '',
  commodity_type: '',
  strategic: '',
  risk: '',
  questionnaire: '',
  otif_band: '',
  approval_gate_status: 'approved', // CR-5556: Default to Approved
  include_inactive: '',
};

const RISK_QUICK_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const QUESTIONNAIRE_QUICK_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'up_to_date', label: 'Up to Date' },
  { value: 'expiring', label: 'Expiring' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'pending', label: 'Pending' },
  { value: 'none', label: 'Not Assigned' },
];

/**
 * CR-6269: the OTIF quick-filter chips — one per band on Admin Setup →
 * Performance → OTIF Settings, named and coloured as configured. The backend
 * otif_band filter resolves the same bands. Was a fixed
 * Excellent / Good / Fair / Poor scale at 95 / 90 / 75.
 */
export interface OTIFQuickOption {
  value: string;
  label: string;
  color?: string;
}

const pct = (v: string) => String(parseFloat(v));

export function otifQuickOptions(bands: OTIFGradeThreshold[]): OTIFQuickOption[] {
  return [
    { value: '', label: 'All' },
    ...bands.map((b) => ({
      value: b.grade,
      label: `${b.display_label || b.label || b.grade} (${pct(b.min_otif_percent)}–${pct(b.max_otif_percent)}%)`,
      color: b.effective_color || b.color,
    })),
  ];
}

interface SearchFilterBarProps {
  searchRef: RefObject<HTMLInputElement | null>;
  showFilters: boolean;
  filters?: SupplierFilters;
  riskCounts?: Record<string, number>;
  questionnaireCounts?: Record<string, number>;
  otifBandCounts?: Record<string, number>;
  /** CR-6269: the viewer's OTIF Grade Thresholds; no bands, no OTIF row. */
  otifBands?: OTIFGradeThreshold[];
  /** CR-8208: the OTIF chip row is hidden when the customer shows OTD only. */
  deliveryMetricMode?: DeliveryMetricMode;
  siteOptions?: { value: string; label: string }[];
  commodityGroupOptions?: { value: string; label: string }[];
  commodityTypeOptions?: { value: string; label: string }[];
  supplierCodeOptions?: { value: string; label: string }[];
  searchQuery?: string;
  onSearch: () => void;
  onSearchKeyDown: (e: React.KeyboardEvent) => void;
  onClearSearch?: () => void;
  onToggleFilters: () => void;
  onFiltersChange?: (filters: SupplierFilters) => void;
}

export function SearchFilterBar({
  searchRef,
  showFilters,
  filters = EMPTY_FILTERS,
  riskCounts = {},
  questionnaireCounts = {},
  otifBandCounts = {},
  otifBands = [],
  deliveryMetricMode = 'both',
  siteOptions = [],
  commodityGroupOptions = [],
  commodityTypeOptions = [],
  supplierCodeOptions = [],
  searchQuery = '',
  onSearch,
  onSearchKeyDown,
  onClearSearch,
  onToggleFilters,
  onFiltersChange,
}: SearchFilterBarProps) {
  // CR-4802: filters this tenant shows — one answer for every panel.
  const fv = useFilterVisibility();
  // CR-5556: Count active filters, excluding the default approval_gate_status='approved'
  const activeFilterCount = Object.entries(filters).filter(([key, value]) => {
    if (!value) return false;
    // Don't count default approval_gate_status as an active filter
    if (key === 'approval_gate_status' && value === 'approved') return false;
    return true;
  }).length;
  // Hide the Risk / Questionnaire quick-filter rows when the matching supplier
  // card field is turned off in the admin "Supplier Card" visibility config.
  const { isSupplierCardFieldEnabled } = useMenuSettings();
  const showRiskRow = isSupplierCardFieldEnabled('risk_badge');
  const showQuestionnaireRow = isSupplierCardFieldEnabled('questionnaire_status');
  const showOtifRow = isSupplierCardFieldEnabled('otif_quick_filter') && otifBands.length > 0
    && showsOtif(deliveryMetricMode);
  const otifOptions = otifQuickOptions(otifBands);

  const handleFilterChange = (key: keyof SupplierFilters, value: string) => {
    if (key === 'commodity_group') {
      onFiltersChange?.({ ...filters, commodity_group: value, commodity_type: '' });
    } else {
      onFiltersChange?.({ ...filters, [key]: value });
    }
  };

  // CR-6993: "Clear filters" used to reset to EMPTY_FILTERS, which re-applies
  // the default approval_gate_status='approved' — so clearing never revealed
  // the suppliers that default was hiding. An explicit clear now removes it too.
  const handleClearFilters = () => {
    onFiltersChange?.({ ...EMPTY_FILTERS, approval_gate_status: '' });
  };

  // CR-6993: the page lands with approval_gate_status='approved' applied but
  // deliberately uncounted in activeFilterCount, so nothing on screen said that
  // non-approved suppliers were being hidden. Surface it as a removable chip so
  // the filter is visible without opening the panel and clearable in one click.
  const approvalFilterLabel = filters.approval_gate_status
    ? APPROVAL_GATE_STATUS_OPTIONS.find((o) => o.value === filters.approval_gate_status)?.label
    : undefined;

  return (
    <div className="space-y-4">
      {/* Search bar + filter toggle */}
      <div className="flex gap-4 items-center">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            ref={searchRef}
            placeholder="Search suppliers..."
            onKeyDown={onSearchKeyDown}
            className="pl-10 pr-10 border border-input bg-background"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => {
                if (searchRef.current) searchRef.current.value = '';
                onClearSearch?.();
              }}
              aria-label="Clear search"
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Button onClick={onSearch} variant="outline">
          <Search className="h-4 w-4" />
        </Button>
        <Button
          variant={showFilters ? 'default' : 'outline'}
          onClick={onToggleFilters}
        >
          <Filter className="h-4 w-4 mr-2" />
          Filters
          {activeFilterCount > 0 && (
            <span className="ml-2 bg-white text-primary rounded-full w-5 h-5 flex items-center justify-center text-xs">
              {activeFilterCount}
            </span>
          )}
        </Button>
      </div>

      {/* Quick filter chip rows — Risk + Questionnaire Status + OTIF band.
          Each row is hidden when its supplier-card field is disabled. */}
      {(showRiskRow || showQuestionnaireRow || showOtifRow) && (
      <div className="space-y-2">
        {showRiskRow && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium text-foreground w-24 shrink-0">Risk</span>
          {RISK_QUICK_OPTIONS.map((opt) => {
            const count = riskCounts[opt.value] ?? 0;
            return (
              <button
                key={opt.value || 'all-risk'}
                type="button"
                onClick={() => handleFilterChange('risk', opt.value)}
                className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                  filters.risk === opt.value
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-foreground border-border hover:bg-accent'
                }`}
              >
                {opt.label} ({count})
              </button>
            );
          })}
        </div>
        )}
        {showQuestionnaireRow && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium text-foreground w-24 shrink-0">Questionnaire</span>
          {QUESTIONNAIRE_QUICK_OPTIONS.map((opt) => {
            const count = questionnaireCounts[opt.value] ?? 0;
            return (
              <button
                key={opt.value || 'all-q'}
                type="button"
                onClick={() => handleFilterChange('questionnaire', opt.value)}
                className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                  filters.questionnaire === opt.value
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-foreground border-border hover:bg-accent'
                }`}
              >
                {opt.label} ({count})
              </button>
            );
          })}
        </div>
        )}
        {showOtifRow && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium text-foreground w-24 shrink-0">OTIF</span>
          {otifOptions.map((opt) => {
            const count = otifBandCounts[opt.value] ?? 0;
            return (
              <button
                key={opt.value || 'all-otif'}
                type="button"
                onClick={() => handleFilterChange('otif_band', opt.value)}
                className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                  filters.otif_band === opt.value
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-foreground border-border hover:bg-accent'
                }`}
              >
                {opt.color && (
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: opt.color }} aria-hidden="true" />
                )}
                {opt.label} ({count})
              </button>
            );
          })}
        </div>
        )}
      </div>
      )}

      {/* CR-6993: active approval-gate filter, shown outside the quick-filter
          block so it stays visible even when every quick-filter row is hidden
          by the admin Supplier Card config. */}
      {approvalFilterLabel && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium text-foreground w-24 shrink-0">Showing</span>
          <button
            type="button"
            onClick={() => handleFilterChange('approval_gate_status', '')}
            title="Remove this filter to show suppliers at every approval stage"
            aria-label={`Remove ${approvalFilterLabel} only filter`}
            className="px-3 py-1 text-xs font-medium rounded-full border bg-primary text-primary-foreground border-primary inline-flex items-center gap-1.5 transition-colors hover:bg-primary/90"
          >
            {approvalFilterLabel} only
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      {/* Inline collapsible filter panel (matching Purchasing page design) */}
      {showFilters && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Filter Suppliers</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Company — customer-side hierarchy. */}
            <FilterSection title="Company" bare>
              <FilterGrid>
                <FilterField
                  label={<>{'Company Site'}{siteOptions.length === 0 && <span className="ml-1 text-xs text-muted-foreground">(none configured)</span>}</>}
                >
                  {siteOptions.length === 0 ? (
                    <div className={EMPTY_OPTION_CLASS}>
                      No company sites available
                    </div>
                  ) : (
                    <SearchableSelect
                      options={siteOptions}
                      value={filters.site}
                      onChange={(val) => handleFilterChange('site', val)}
                      placeholder="All Company Sites"
                    />
                  )}
                </FilterField>
              </FilterGrid>
            </FilterSection>

            {/* Supplier — identity of the supplier itself, including the
                strategic-only toggle which is a supplier attribute. */}
            <FilterSection title="Supplier" bare>
              <FilterGrid>
                {fv.supplierCode && (
                <FilterField
                  label={<>{'Supplier Code'}{supplierCodeOptions.length === 0 && <span className="ml-1 text-xs text-muted-foreground">(none configured)</span>}</>}
                >
                  {supplierCodeOptions.length === 0 ? (
                    <div className={EMPTY_OPTION_CLASS}>
                      No supplier codes available
                    </div>
                  ) : (
                    <SearchableSelect
                      options={supplierCodeOptions}
                      value={filters.supplier_code}
                      onChange={(val) => handleFilterChange('supplier_code', val)}
                      placeholder="Search by code or name…"
                    />
                  )}
                </FilterField>
                )}

                {/* Strategic */}
                <div className="flex items-end pb-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.strategic === 'yes'}
                      onChange={(e) => handleFilterChange('strategic', e.target.checked ? 'yes' : '')}
                      className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                    />
                    <span className="text-sm font-medium">Strategic Only</span>
                  </label>
                </div>

                {/* Inactive suppliers are hidden by default. They still own
                    their POs and their code is dropped from the ERP feed
                    filter, so they must be findable to diagnose or reverse. */}
                <div className="flex items-end pb-2">
                  <label
                    className="flex items-center gap-2 cursor-pointer"
                    title="Show deactivated suppliers alongside active ones"
                  >
                    <input
                      type="checkbox"
                      checked={filters.include_inactive === 'yes'}
                      onChange={(e) => handleFilterChange('include_inactive', e.target.checked ? 'yes' : '')}
                      className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                    />
                    <span className="text-sm font-medium">Include Inactive</span>
                  </label>
                </div>
              </FilterGrid>
            </FilterSection>

            {/* CR-4802: the whole group is tenant-gated; drop the heading when nothing is shown. */}
            {(fv.commodity) && (<>
              {/* Classification — the app-wide name for the commodity pair.
                  Commodity Group still resets Commodity Type via handleFilterChange. */}
              <FilterSection title="Classification" bare>
                <FilterGrid>
                  {fv.commodity && (<>
                  <FilterField
                    label={<>{'Commodity Group'}{commodityGroupOptions.length === 0 && <span className="ml-1 text-xs text-muted-foreground">(none configured)</span>}</>}
                  >
                    {commodityGroupOptions.length === 0 ? (
                      <div className={EMPTY_OPTION_CLASS}>
                        No commodity groups available
                      </div>
                    ) : (
                      <select
                        value={filters.commodity_group}
                        onChange={(e) => handleFilterChange('commodity_group', e.target.value)}
                        className={filterControlClass}
                      >
                        <option value="">All Commodity Groups</option>
                        {commodityGroupOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    )}
                  </FilterField>

                  <FilterField
                    label={<>{'Commodity Type'}{commodityTypeOptions.length === 0 && <span className="ml-1 text-xs text-muted-foreground">(none configured)</span>}</>}
                  >
                    {commodityTypeOptions.length === 0 ? (
                      <div className={EMPTY_OPTION_CLASS}>
                        No commodity types available
                      </div>
                    ) : (
                      <select
                        value={filters.commodity_type}
                        onChange={(e) => handleFilterChange('commodity_type', e.target.value)}
                        className={filterControlClass}
                      >
                        <option value="">All Commodity Types</option>
                        {commodityTypeOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    )}
                  </FilterField>
                  </>)}
                </FilterGrid>
              </FilterSection>
            </>)}

            {/* Status & Risk — the full-precision equivalents of the quick-filter
                chip rows, plus the approval gate. */}
            <FilterSection title="Status & Risk" bare>
              <FilterGrid>
                <FilterField label="Risk Level">
                  <select
                    value={filters.risk}
                    onChange={(e) => handleFilterChange('risk', e.target.value)}
                    className={filterControlClass}
                  >
                    {RISK_QUICK_OPTIONS.map((opt) => (
                      <option key={opt.value || 'all-risk'} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </FilterField>

                <FilterField label="Questionnaire Status">
                  <select
                    value={filters.questionnaire}
                    onChange={(e) => handleFilterChange('questionnaire', e.target.value)}
                    className={filterControlClass}
                  >
                    {QUESTIONNAIRE_QUICK_OPTIONS.map((opt) => (
                      <option key={opt.value || 'all-q'} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </FilterField>

                {/* CR-5556: Approval Gate Status */}
                <FilterField label="Approval Gate Status">
                  <select
                    value={filters.approval_gate_status}
                    onChange={(e) => handleFilterChange('approval_gate_status', e.target.value)}
                    className={filterControlClass}
                  >
                    {APPROVAL_GATE_STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value || 'all-approval'} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </FilterField>
              </FilterGrid>
            </FilterSection>

            {/* Search + Clear Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              {activeFilterCount > 0 && (
                <Button variant="outline" onClick={handleClearFilters} className="px-6">
                  <X className="h-4 w-4 mr-2" />
                  Clear All
                </Button>
              )}
              <Button onClick={onSearch} className="px-6">
                <Search className="h-4 w-4 mr-2" />
                Search
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
